import type { ColorScheme, SelectOption } from '@hivewatch/dark'
import { Select } from '@hivewatch/dark'

import { FilterOptionI } from '@/utils/filterDrawers'

interface SelectFilterRowIProps<T> {
  label: string
  placeholder: string
  colorScheme: ColorScheme
  options: FilterOptionI<T>[]
  value: FilterOptionI<T> | null | undefined
  onChange: (value: FilterOptionI<T> | null) => void
  'data-testid'?: string
}

/**
 * Single-select Filter Row on the dark Select (Rule Action, Clips). Options
 * are keyed by `String(value)` - safe for enum and boolean values.
 */
export const SelectFilterRow = <T,>({
  label,
  placeholder,
  colorScheme,
  options,
  value,
  onChange,
  'data-testid': dataTestId,
}: SelectFilterRowIProps<T>) => {
  const toKey = (option: FilterOptionI<T>) => String(option.value)

  const selectOptions: SelectOption[] = options.map((option) => ({
    value: toKey(option),
    label: option.label,
  }))

  const selectedOption =
    value != null
      ? selectOptions.find((option) => option.value === toKey(value)) ?? null
      : null

  const handleChange = (option: SelectOption | null) => {
    if (option === null) {
      onChange(null)
      return
    }
    onChange(options.find((o) => toKey(o) === option.value) ?? null)
  }

  return (
    <Select
      colorScheme={colorScheme}
      data-testid={dataTestId}
      label={label}
      onChange={handleChange}
      options={selectOptions}
      placeholder={placeholder}
      value={selectedOption}
    />
  )
}
